import * as React from "react";
import Parse from "parse";
import {
  Card,
  Checkbox,
  DatePicker,
  Select,
  Typography,
  Button,
  Input,
  Modal,
  Space,
  Tooltip,
  message,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { AdminLayout, usePermission } from "@opendash/core";
import { AdminToolbar } from "@opendash/ui";
import { Icon } from "@opendash/icons";
import {
  AnalyticsData,
  GroupMode,
  downloadCsv,
  toCsv,
} from "./analyticsHelpers";
import { AnalyticsDashboardView } from "./AnalyticsDashboardView";

const { Text } = Typography;
const { RangePicker } = DatePicker;

type TrackingMode = "off" | "anonymous" | "full";

const TRACKING_MODE_OPTIONS: {
  value: TrackingMode;
  label: string;
  hint: string;
}[] = [
  { value: "off", label: "Aus", hint: "Kein Tracking, kein Banner." },
  {
    value: "anonymous",
    label: "Anonym",
    hint: "Reichweitenmessung ohne Identifier und ohne Banner.",
  },
  {
    value: "full",
    label: "Mit Banner",
    hint: "Anonyme Basis plus Opt-in-Banner für erweitertes Tracking.",
  },
];

interface TenantInfo {
  tenantId: string;
  hostname: string;
  title: string;
  analyticsMode: TrackingMode;
  analyticsVisibleForTenant: boolean;
}

export const AnalyticsAdminRoute = React.memo(function AnalyticsAdminRoute() {
  const isOdAdmin = usePermission("od-admin");
  const currentUser = Parse.User.current();
  const ownTenantId: string | undefined = currentUser?.get("tenant")?.id;
  const isTenantAdmin: boolean = currentUser?.get("tenantAdmin") === true;

  const [tenants, setTenants] = React.useState<TenantInfo[]>([]);
  const [selectedTenant, setSelectedTenant] = React.useState<string>(
    isOdAdmin ? "__all__" : ownTenantId || "__all__"
  );
  const [dateRange, setDateRange] = React.useState<[Dayjs, Dayjs]>([
    dayjs().subtract(30, "day"),
    dayjs(),
  ]);
  const [data, setData] = React.useState<AnalyticsData | null>(null);
  const [tenantTitle, setTenantTitle] = React.useState<string>("");
  const [loading, setLoading] = React.useState(false);
  const [groupMode, setGroupMode] = React.useState<GroupMode>("day");

  const [shareModalOpen, setShareModalOpen] = React.useState(false);
  const [shareTenant, setShareTenant] = React.useState<string>("");
  const [shareLabel, setShareLabel] = React.useState<string>("");
  const [sharePassword, setSharePassword] = React.useState<string>("");
  const [shareSubmitting, setShareSubmitting] = React.useState(false);

  const [togglingEnabled, setTogglingEnabled] = React.useState(false);
  const [togglingVisibility, setTogglingVisibility] = React.useState(false);
  const [accessDenied, setAccessDenied] = React.useState(false);
  const [refreshKey, setRefreshKey] = React.useState(0);

  React.useEffect(() => {
    if (!isOdAdmin) return;
    (async () => {
      try {
        const result = await Parse.Cloud.run("analyticsGetTenants");
        setTenants(result?.tenants || []);
      } catch (err) {
        console.error("Tenant list load failed:", err);
      }
    })();
  }, [isOdAdmin]);

  const accessAllowed = isOdAdmin || (isTenantAdmin && !!ownTenantId);

  React.useEffect(() => {
    if (!accessAllowed) {
      setData(null);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const result = await Parse.Cloud.run("analyticsGetData", {
          from: dateRange[0].startOf("day").toISOString(),
          to: dateRange[1].endOf("day").toISOString(),
          groupMode,
          tenantId: isOdAdmin ? selectedTenant : undefined,
        });
        setData(result.data);
        setTenantTitle(result.tenantTitle || "");
        setAccessDenied(false);
      } catch (err: any) {
        console.error("Analytics load failed:", err);
        setData(null);
        // Der Server lehnt ab, wenn das eigene Stadtwerk nicht freigeschaltet
        // ist. Dann eine Erklärung zeigen statt einer leeren Seite.
        setAccessDenied(err?.code === Parse.Error.OPERATION_FORBIDDEN);
      }
      setLoading(false);
    })();
  }, [selectedTenant, dateRange, groupMode, isOdAdmin, accessAllowed, refreshKey]);

  const tenantLabel = tenantTitle || "Alle Stadtwerke";

  const selectedTenantMode: TrackingMode =
    tenants.find((t) => t.tenantId === selectedTenant)?.analyticsMode ?? "off";

  const applyMode = async (mode: TrackingMode) => {
    setTogglingEnabled(true);
    try {
      await Parse.Cloud.run("analyticsSetTenantEnabled", {
        tenantId: selectedTenant,
        mode,
      });
      setTenants((prev) =>
        prev.map((t) =>
          t.tenantId === selectedTenant ? { ...t, analyticsMode: mode } : t
        )
      );
      message.success(
        `Tracking auf "${
          TRACKING_MODE_OPTIONS.find((o) => o.value === mode)?.label
        }" gesetzt`
      );
    } catch (err: any) {
      console.error(err);
      message.error(err?.message || "Konnte nicht geändert werden");
    }
    setTogglingEnabled(false);
  };

  const selectedTenantVisible =
    tenants.find((t) => t.tenantId === selectedTenant)
      ?.analyticsVisibleForTenant ?? false;

  const handleToggleVisibility = async (visible: boolean) => {
    if (!selectedTenant || selectedTenant === "__all__") return;
    setTogglingVisibility(true);
    try {
      await Parse.Cloud.run("analyticsSetTenantVisibility", {
        tenantId: selectedTenant,
        visible,
      });
      setTenants((prev) =>
        prev.map((t) =>
          t.tenantId === selectedTenant
            ? { ...t, analyticsVisibleForTenant: visible }
            : t
        )
      );
      message.success(
        visible
          ? "Stadtwerk sieht die Auswertung jetzt im Menü"
          : "Zugriff für das Stadtwerk entzogen"
      );
    } catch (err: any) {
      console.error(err);
      message.error(err?.message || "Konnte nicht geändert werden");
    }
    setTogglingVisibility(false);
  };

  const handleChangeMode = (mode: TrackingMode) => {
    if (!selectedTenant || selectedTenant === "__all__") return;

    // "full" setzt Identifier im Browser der Besucher und braucht eine
    // Rechtsgrundlage — deshalb bewusst mit Rückfrage.
    if (mode === "full") {
      Modal.confirm({
        title: "Erweitertes Tracking aktivieren?",
        okText: "Aktivieren",
        cancelText: "Abbrechen",
        okButtonProps: { danger: true },
        content: (
          <Space direction="vertical">
            <Text>
              Für alle Besucher dieses Stadtwerks werden ab sofort ein
              Consent-Banner angezeigt und – nach Zustimmung – visitorId,
              sessionId, User-Agent, Referrer, Bildschirmgröße und Verweildauer
              gespeichert.
            </Text>
            <Text strong>
              Die Datenschutzerklärung dieses Stadtwerks muss das abdecken.
            </Text>
          </Space>
        ),
        onOk: () => applyMode(mode),
      });
      return;
    }

    applyMode(mode);
  };

  const handleExportCsv = () => {
    if (!data) return;
    const csv = toCsv(data, tenantLabel);
    const from = dateRange[0].format("YYYY-MM-DD");
    const to = dateRange[1].format("YYYY-MM-DD");
    const safeName = tenantLabel.replace(/[^a-zA-Z0-9äöüÄÖÜß\-_ ]/g, "");
    downloadCsv(csv, `Analytics_${safeName}_${from}_${to}.csv`);
  };

  const openShareModal = () => {
    setShareTenant(
      isOdAdmin && selectedTenant !== "__all__" ? selectedTenant : ""
    );
    setShareLabel("");
    setSharePassword("");
    setShareModalOpen(true);
  };

  const handleCreateShareLink = async () => {
    if (!shareTenant || !sharePassword) {
      message.warning("Bitte Stadtwerk und Passwort wählen");
      return;
    }
    setShareSubmitting(true);
    try {
      const result = await Parse.Cloud.run("analyticsShareCreate", {
        tenantId: shareTenant,
        password: sharePassword,
        label: shareLabel || undefined,
      });
      const token = result?.token;
      if (!token) throw new Error("Kein Token erhalten");
      const url = `${window.location.origin}/analytics-share?token=${encodeURIComponent(
        token
      )}`;
      await navigator.clipboard.writeText(url);
      message.success("Link wurde in die Zwischenablage kopiert!");
      setShareModalOpen(false);
    } catch (err: any) {
      console.error(err);
      message.error(
        err?.message || "Sharing-Link konnte nicht erstellt werden"
      );
    }
    setShareSubmitting(false);
  };

  if (!accessAllowed) {
    return (
      <AdminLayout contentPadding>
        <AdminToolbar title="Analytics" />
        <Text type="secondary">
          Für Ihren Account ist keine Auswertung verfügbar — kein Stadtwerk
          zugeordnet.
        </Text>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout contentPadding>
      <AdminToolbar
        title="Analytics"
        description={
          tenantLabel !== "Alle Stadtwerke"
            ? `Auswertung für ${tenantLabel}`
            : "Auswertung über alle Stadtwerke"
        }
        actions={[
          <Button
            key="refresh"
            icon={<Icon icon="fa:sync" />}
            onClick={() => setRefreshKey((k) => k + 1)}
            loading={loading}
          >
            Aktualisieren
          </Button>,
          <Button
            key="csv"
            icon={<Icon icon="fa:download" />}
            onClick={handleExportCsv}
            disabled={!data}
          >
            CSV Export
          </Button>,
          isOdAdmin && (
            <Button key="share" type="primary" onClick={openShareModal}>
              Sharing-Link erstellen
            </Button>
          ),
        ].filter(Boolean)}
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap size="large">
          {isOdAdmin && (
            <Space>
              <Text type="secondary">Stadtwerk</Text>
              <Select
                style={{ minWidth: 220 }}
                value={selectedTenant}
                onChange={(v) => setSelectedTenant(v)}
                showSearch
                placeholder="Stadtwerk wählen"
                filterOption={(input, option) =>
                  String(option?.children ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                <Select.Option value="__all__">Alle Stadtwerke</Select.Option>
                {tenants.map((t) => (
                  <Select.Option key={t.tenantId} value={t.tenantId}>
                    {t.title}
                  </Select.Option>
                ))}
              </Select>
            </Space>
          )}
          <Space>
            <Text type="secondary">Zeitraum</Text>
            <RangePicker
              value={dateRange}
              onChange={(v) => {
                if (v && v[0] && v[1]) setDateRange([v[0], v[1]]);
              }}
            />
          </Space>
          {isOdAdmin && selectedTenant !== "__all__" && (
            <Space>
              <Text type="secondary">Tracking</Text>
              <Select<TrackingMode>
                value={selectedTenantMode}
                onChange={handleChangeMode}
                loading={togglingEnabled}
                disabled={togglingEnabled}
                style={{ width: 150 }}
                options={TRACKING_MODE_OPTIONS.map((o) => ({
                  value: o.value,
                  label: o.label,
                }))}
              />
              <Tooltip
                title={
                  TRACKING_MODE_OPTIONS.find(
                    (o) => o.value === selectedTenantMode
                  )?.hint
                }
              >
                <Icon icon="fa:info-circle" />
              </Tooltip>
            </Space>
          )}
          {isOdAdmin && selectedTenant !== "__all__" && (
            <Tooltip title="Das Stadtwerk sieht die Auswertung dann selbst im Admin-Menü — unabhängig vom Sharing-Link.">
              <Checkbox
                checked={selectedTenantVisible}
                disabled={togglingVisibility}
                onChange={(e) => handleToggleVisibility(e.target.checked)}
              >
                Für Stadtwerk sichtbar
              </Checkbox>
            </Tooltip>
          )}
        </Space>
      </Card>

      {accessDenied && !loading && (
        <Text type="secondary">
          Für Ihr Stadtwerk ist die Auswertung nicht freigeschaltet. Bitte
          wenden Sie sich an die ASEW.
        </Text>
      )}

      {data && (
        <AnalyticsDashboardView
          data={data}
          loading={loading}
          groupMode={groupMode}
          onGroupModeChange={setGroupMode}
        />
      )}

      <Modal
        title="Sharing-Link erstellen"
        open={shareModalOpen}
        onCancel={() => setShareModalOpen(false)}
        onOk={handleCreateShareLink}
        okText="Link erstellen & kopieren"
        cancelText="Abbrechen"
        confirmLoading={shareSubmitting}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              Stadtwerk
            </Text>
            <Select
              style={{ width: "100%" }}
              value={shareTenant || undefined}
              onChange={(v) => setShareTenant(v)}
              placeholder="Stadtwerk wählen"
              showSearch
              filterOption={(input, option) =>
                String(option?.children ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {tenants.map((t) => (
                <Select.Option key={t.tenantId} value={t.tenantId}>
                  {t.title}
                </Select.Option>
              ))}
            </Select>
          </div>
          <div>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              Bezeichnung (optional)
            </Text>
            <Input
              placeholder="z. B. Quartalsbericht Q1"
              value={shareLabel}
              onChange={(e) => setShareLabel(e.target.value)}
            />
          </div>
          <div>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              Passwort für diesen Link
            </Text>
            <Input.Password
              placeholder="Passwort festlegen"
              value={sharePassword}
              onChange={(e) => setSharePassword(e.target.value)}
            />
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Der Empfänger sieht nur die Daten des gewählten Stadtwerks und muss
            das Passwort eingeben. Das Passwort wird gehasht gespeichert und
            kann nicht wiederhergestellt werden.
          </Text>
        </Space>
      </Modal>
    </AdminLayout>
  );
});
